<script setup>
import { computed, onMounted, onBeforeUnmount, reactive, ref } from "vue";
import { Plus, Search, Pencil, Trash2, Building2 } from "@lucide/vue";
import { api } from "../api";
import { useSessionStore } from "../stores/session";
import { useRouter } from "vue-router";
import PageHeader from "../components/PageHeader.vue";
import TablePager from "../components/TablePager.vue";
import UiDrawer from "../ui/UiDrawer.vue";
import UiToast from "../ui/UiToast.vue";
import UiButton from "../ui/UiButton.vue";
import UiConfirmDrawer from "../ui/UiConfirmDrawer.vue";
import UiInput from "../ui/UiInput.vue";
import UiPhoneInput from "../ui/UiPhoneInput.vue";
const session = useSessionStore(),
  router = useRouter(),
  rows = ref([]),
  search = ref(""),
  page = ref(1),
  modal = ref(false),
  toast = ref(""),
  toastType = ref("success"),
  saving = ref(false),
  deleting = ref(false),
  deleteTarget = ref(null),
  perPage = 12;
const blank = () => ({
    id: null,
    name: "",
    code: "",
    contactPhone: "",
    contactEmail: "",
    address: "",
    adminUsername: "",
    adminPassword: "",
    active: true,
  }),
  form = reactive(blank());
const filtered = computed(() =>
    rows.value.filter((r) =>
      `${r.name} ${r.code} ${r.admin_username || ""}`
        .toLocaleLowerCase("tr-TR")
        .includes(search.value.toLocaleLowerCase("tr-TR")),
    ),
  ),
  paged = computed(() =>
    filtered.value.slice((page.value - 1) * perPage, page.value * perPage),
  );
function show(m, t = "success") {
  toast.value = m;
  toastType.value = t;
  setTimeout(() => (toast.value = ""), 3000);
}
async function load() {
  try {
    rows.value = (await api("/api/schools")).schools || [];
    session.schools = rows.value;
  } catch (e) {
    show(e.message, "error");
  }
}
function open(r = null) {
  Object.assign(form, blank());
  if (r)
    Object.assign(form, {
      id: r.id,
      name: r.name,
      code: r.code,
      contactPhone: r.contact_phone || "",
      contactEmail: r.contact_email || "",
      address: r.address || "",
      adminUsername: r.admin_username || "",
      adminPassword: "",
      active: Boolean(r.active),
    });
  modal.value = true;
}
async function save() {
  saving.value = true;
  try {
    await api(form.id ? `/api/schools/${form.id}` : "/api/schools", {
      method: form.id ? "PUT" : "POST",
      body: JSON.stringify(form),
    });
    modal.value = false;
    show("Okul kaydedildi.");
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    saving.value = false;
  }
}
async function remove(r) {
  deleteTarget.value = r;
}
function openDevices(r) {
  session.selectedSchoolId = String(r.id);
  router.push("/cihazlar");
}
async function confirmRemove() {
  if (!deleteTarget.value) return;
  deleting.value = true;
  try {
    await api(`/api/schools/${deleteTarget.value.id}`, { method: "DELETE" });
    show("Okul silindi, cihazlara sıfırlama isteği gönderildi.");
    deleteTarget.value = null;
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    deleting.value = false;
  }
}
const dt = (v) =>
  new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium" }).format(new Date(v));
const refresh = () => load();
onMounted(() => {
  load();
  window.addEventListener("lopiy:refresh", refresh);
});
onBeforeUnmount(() => window.removeEventListener("lopiy:refresh", refresh));
</script>
<template>
  <PageHeader
    title="Okullar"
    description="Erva Spor Okullarını, panel hesaplarını ve kurum iletişim bilgilerini yönetin."
    ><button class="btn-primary" @click="open()">
      <Plus class="size-4" />Okul Ekle
    </button></PageHeader
  >
  <section class="panel overflow-hidden">
    <div class="toolbar">
      <span class="text-sm font-semibold text-slate-500"
        >{{ filtered.length }} okul</span
      ><label class="relative w-full sm:w-72"
        ><Search class="absolute left-3 top-2.5 size-4 text-slate-400" /><input
          v-model="search"
          class="input pl-9"
          placeholder="Okul, kod veya kullanıcı ara"
      /></label>
    </div>
    <div class="overflow-x-auto">
      <table class="data-table">
        <thead>
          <tr>
            <th>Okul</th>
            <th>Kod</th>
            <th>Panel kullanıcısı</th>
            <th>Telefon</th>
            <th>E-posta</th>
            <th>Cihazlar</th>
            <th>Durum</th>
            <th>Oluşturma</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in paged" :key="r.id">
            <td>
              <div class="flex items-center gap-3">
                <span
                  class="grid size-9 place-items-center rounded-md bg-brand-50 text-brand-600"
                  ><Building2 class="size-4" /></span
                ><strong class="text-ink-900">{{ r.name }}</strong>
              </div>
            </td>
            <td class="font-mono text-xs">{{ r.code }}</td>
            <td>{{ r.admin_username || "-" }}</td>
            <td>{{ r.contact_phone || "-" }}</td>
            <td>{{ r.contact_email || "-" }}</td>
            <td>
              <button
                class="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-xs font-bold text-brand-700 transition hover:bg-brand-100"
                :title="`${r.name} okulunun cihazlarını göster`"
                @click="openDevices(r)"
              >
                {{ r.device_count || 0 }} cihaz
              </button>
            </td>
            <td>
              <span :class="r.active ? 'chip-success' : 'chip-neutral'">{{
                r.active ? "Aktif" : "Pasif"
              }}</span>
            </td>
            <td class="whitespace-nowrap">{{ dt(r.created_at) }}</td>
            <td>
              <div class="flex justify-end gap-1">
                <button class="icon-btn size-8" @click="open(r)">
                  <Pencil class="size-3.5" /></button
                ><button
                  class="icon-btn size-8 hover:text-red-600"
                  @click="remove(r)"
                >
                  <Trash2 class="size-3.5" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePager
      v-model:page="page"
      :total="filtered.length"
      :per-page="perPage"
    />
  </section>
  <UiDrawer
    :open="modal"
    :title="form.id ? 'Okulu düzenle' : 'Yeni okul oluştur'"
    description="Kurum, iletişim ve panel erişim bilgilerini yönetin."
    size="lg"
    @close="modal = false"
    ><form id="school-form" class="grid gap-5 md:grid-cols-2" @submit.prevent="save">
      <label class="field"
        ><span class="field-label">Okul adı</span
        ><UiInput v-model="form.name" required /></label
      ><label class="field"
        ><span class="field-label">Kurum kodu</span
        ><UiInput
          v-model="form.code"
          class="uppercase"
          :required="Boolean(form.id)"
          placeholder="Boşsa otomatik üretilir" /></label
      ><label class="field"
        ><span class="field-label">Panel kullanıcı adı</span
        ><UiInput v-model="form.adminUsername" autocomplete="username" /></label
      ><label class="field"
        ><span class="field-label">Panel şifresi</span
        ><UiInput
          v-model="form.adminPassword"
          type="password"
          autocomplete="new-password"
          :placeholder="form.id ? 'Yalnızca değiştirmek için yeni şifre girin' : ''" /></label
      ><label class="field"
        ><span class="field-label">Telefon</span
        ><UiPhoneInput v-model="form.contactPhone" /></label
      ><label class="field"
        ><span class="field-label">E-posta</span
        ><UiInput v-model="form.contactEmail" type="email" autocomplete="email" /></label
      ><label class="field md:col-span-2"
        ><span class="field-label">Adres</span
        ><textarea v-model="form.address" class="textarea"></textarea></label
      ><label
        class="flex items-center gap-3 rounded-md border p-3 text-sm font-semibold"
        ><input
          v-model="form.active"
          type="checkbox"
          class="size-4 accent-brand-600"
        />Okul aktif</label
      >
    </form>
    <template #footer>
      <UiButton variant="secondary" @click="modal = false">Vazgeç</UiButton>
      <UiButton type="submit" form="school-form" :loading="saving">Okulu Kaydet</UiButton>
    </template>
  </UiDrawer>
  <UiConfirmDrawer
    :open="Boolean(deleteTarget)"
    title="Okulu ve bağlı kayıtları sil"
    :description="deleteTarget ? `${deleteTarget.name} ile ilişkili öğrenci, kart, cihaz ve log kayıtları silinecek; cihazlara sıfırlama isteği gönderilecek.` : ''"
    confirm-label="Okulu Sil"
    :loading="deleting"
    @close="deleteTarget = null"
    @confirm="confirmRemove"
  />
  <UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
